import { ThatchEmployerSetup } from '../../../services/teamcompanies/teamcompanies.service';
import { ThatchHelpDialogComponent } from '../thatch-help-dialog.component';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { TeamprogramsService } from '../../../services/teamprograms/teamprograms.service';
import { Category, TeamsService } from '../../../services/teams/teams.service';
import { StepDefinition, StepDialogComponent } from '../step-dialog/step-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { Plan, StripeService } from '../../../services/stripe/stripe.service';
import {
  PayrollProviderConnectionResponse,
  PayrollService,
  UkgDeductionCode,
} from '../../../services/payroll/payroll.service';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../../../services/session/session.service';
import {
  BenefitProductItem,
  BenefitProductsLoadResult,
  AssignableBenefitProductItem,
  PayrollBenefitsAssignmentService,
} from '../../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import {
  BenefitCategoryTab,
  buildPayrollBenefitSectionDefinitions,
  PayrollBenefitSectionDefinition,
  PayrollBenefitSubcategoryDefinitionsByCategory,
} from '../../../services/payroll-benefits-assignment/payroll-benefit-categories';
import {
  CustomReimbursementRolloverResolutionAction,
  CustomReimbursementRolloverResolutionDialogComponent,
  CustomReimbursementRolloverResolutionDialogResult,
} from './custom-reimbursement-rollover-resolution-dialog.component';
import { ManageWellspaceProductSelection, TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import {
  GroupProductOnboardingRequestDialogComponent,
  GroupProductOnboardingRequestDialogResult,
} from '../group-product-onboarding-request-dialog/group-product-onboarding-request-dialog.component';
import {
  REIMBURSEMENT_CATEGORY_PRESETS,
  ReimbursementCategoryPresetDefinition,
  ReimbursementCategoryPresetKey,
} from './reimbursement-category-presets';
import { PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG } from '../payroll-admin-feature-flags';
import { PayrollWorkspaceContext } from '../../../services/payroll-access/payroll-access.service';
import { FeeService } from '../../../services/fee/fee.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';

export type EditBenefitsDialogData = {
  companyId?: string;
  teamId?: string;
  teamName?: string;
  workspaceContext?: PayrollWorkspaceContext;
  company?: any;
  availableBenefitProductIds?: string[];
  teamManager?: boolean;
};

export type EditBenefitsDialogResult =
  | { saved: false }
  | { saved: true };

type BonusProgramType = 'welcome' | 'birthday' | 'anniversary';

interface BonusSetting {
  id: string;
  enabled: boolean;
  name: string;
  description: string;
  type: BonusProgramType;
  amount: number;
  message: string;
}

interface ReimbursedCategory {
  taxable: boolean;
  toolTip: string;
  category: string;
  selected: boolean;
}

interface BenefitSection {
  id: string;
  title: string;
  products: BenefitProductItem[];
}

interface BenefitProductSourceTeamOption {
  id: string;
  label: string;
}

type CustomReimbursementFrequency = 'monthly';

interface CustomReimbursementCategory {
  clientKey: string;
  id?: string;
  presetKey?: ReimbursementCategoryPresetKey;
  name: string;
  spendCategories: string[];
  vendorProductIds: string[];
  maxAmountCents: number;
  frequency: CustomReimbursementFrequency;
  rollover: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CustomReimbursementDraft {
  name: string;
  maxAmountDollars: number;
  rollover: boolean;
  spendCategories: string[];
  vendorProductIds: string[];
}

interface CustomReimbursementCategoryResolution {
  sourceCategoryId: string;
  action: CustomReimbursementRolloverResolutionAction;
  targetCategoryId?: string;
}

interface PresetReimbursementCategoryState {
  preset: ReimbursementCategoryPresetDefinition;
  category: CustomReimbursementCategory | null;
  enabled: boolean;
}

@Component({
    selector: 'app-edit-benefits-dialog',
    templateUrl: './edit-benefits-dialog.component.html',
    styleUrls: ['./edit-benefits-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditBenefitsDialogComponent extends StepDialogComponent implements OnDestroy, OnInit {
  thatchSetup: ThatchEmployerSetup | null = null;
  private thatchPreviouslySelected = new Set<string>();
  isThatchSetupBlocked(product: BenefitProductItem): boolean {
    return product?.vendorSlug === 'thatch' && this.thatchSetup?.selectable !== true
      && !this.thatchPreviouslySelected.has(product.id);
  }
  private async loadThatchSetup(): Promise<void> {
    this.thatchSetup = null;
    const companyId = this.data?.companyId;
    if (!companyId || !this.benefitProducts.some(p => p.vendorSlug === 'thatch')) return;
    this.thatchPreviouslySelected = new Set(this.selectedBenefitProductIds);
    try {
      const setup = await this.teamCompaniesService.getThatchEmployerSetup(companyId);
      if (companyId === this.data?.companyId) this.thatchSetup = setup;
    } catch { /* Unknown setup blocks only new Thatch selection; the dialog provides retry. */ }
  }

  private readonly alphabeticalCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
  private readonly broadExcludedTransactionTermSet = new Set(['pay', 'prime', 'store']);
  private readonly reimbursementCategoryPresetLookup = new Map(
    REIMBURSEMENT_CATEGORY_PRESETS.map((preset) => [preset.key, preset] as const)
  );

  isSaving = false;
  selectedAllowance = 0;
  private initialAllowance = 0;
  loadError = '';
  saveError = '';
  private hasLoadedBonuses = false;
  private readonly savedSections = new Set<string>();
  private readonly initialBonusSettings = new Map<BonusProgramType, string>();
  private destroyed = false;
  isLoadingBonuses = false;
  isLoadingReimbursed = false;
  reimbursementsEnabled = true;
  private initialReimbursementsEnabled = true;
  isLoadingBenefitsProducts = false;
  isLoadingPayrollDeductionCodes = false;
  isSourceTeamOptionsLoading = false;
  isSourceTeamProductPreviewLoading = false;
  isPayrollDeductionFeatureEnabled = false;

  reimbursedCategories: ReimbursedCategory[] = [];
  payrollDeductionCodes: UkgDeductionCode[] = [];
  payrollDeductionCodesLoadError = '';
  private initialReimbursedCategories: string[] = [];
  private hasLoadedReimbursedCategories = false;
  private currentTeamPlan: Plan = Plan.Free;
  private teamInfo: any;
  private companyInfo: any;
  private hasLoadedBenefitProducts = false;
  private companyProductIdsInOrder: string[] = [];
  private alwaysActiveProductIds = new Set<string>();
  private preservedNativeProductIds: string[] = [];
  private initialSelectedBenefitProductIds: string[] = [];
  private initialAllowanceEligibleProductIds: string[] = [];
  private initialProductPayrollDeductionMapSnapshot = '';
  private initialCustomReimbursementCategoriesSnapshot = '';
  private initialExcludedTransactionTermsSnapshot = '';
  private initialIncludedTransactionTermsSnapshot = '';
  private pendingCustomReimbursementCategoryResolutions: CustomReimbursementCategoryResolution[] = [];
  private reimbursementProductLookup = new Map<string, AssignableBenefitProductItem>();
  private pendingOnboardingRequestProductIds = new Set<string>();
  private hasSubmittedGroupProductOnboardingRequest = false;
  private hasAttemptedPayrollDeductionCodesLoad = false;
  private payrollProviderConnection: PayrollProviderConnectionResponse | null = null;
  private payrollDeductionCodesRequest?: Promise<void>;
  private payrollDeductionFeatureFlagRequest?: Promise<void>;
  private missingBenefitProductImageQueue: BenefitProductItem[] = [];
  private missingBenefitProductImageActiveRequests = 0;
  private missingBenefitProductImageLoadVersion = 0;
  private stripeProductImageRequests = new Map<string, Promise<string>>();
  private readonly missingBenefitProductImageMaxConcurrentLoads = 4;
  private sourceTeamProductLoadVersion = 0;

  benefitProducts: BenefitProductItem[] = [];
  customReimbursementCategories: CustomReimbursementCategory[] = [];
  excludedTransactionTerms: string[] = [];
  includedTransactionTerms: string[] = [];
  customReimbursementDraft: CustomReimbursementDraft = this.createEmptyCustomReimbursementDraft();
  editingCustomReimbursementCategoryKey: string | null = null;
  isCreatingCustomReimbursementCategory = false;
  selectedBenefitProductIds = new Set<string>();
  sourceTeamOptions: BenefitProductSourceTeamOption[] = [];
  selectedSourceTeamId = '';
  isSourceTeamProductHelperExpanded = false;
  sourceTeamOptionsError = '';
  sourceTeamProductPreviewError = '';
  sourceTeamProductPreviewProductIds: string[] = [];
  sourceTeamProductPreviewAddedProductIds: string[] = [];
  sourceTeamProductPreviewOverlappingProductIds: string[] = [];
  sourceTeamProductPreviewRemovedProductIds: string[] = [];
  sourceTeamProductPreviewSkippedProductIds: string[] = [];
  sourceTeamProductPreviewHiddenProductIds: string[] = [];
  sourceTeamProductPreviewAllowanceEligibleProductIds: string[] = [];
  sourceTeamProductPreviewApplied = false;

  subcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {};
  readonly reimbursementCategoryPresets = REIMBURSEMENT_CATEGORY_PRESETS;
  readonly excludedTransactionSeparatorKeysCodes = [ENTER, COMMA] as const;
  readonly includedTransactionSeparatorKeysCodes = [ENTER, COMMA] as const;

  bonusSettings: BonusSetting[] = [
    {
      id: '',
      enabled: false,
      name: 'Welcome Bonus',
      description: 'Give employees a bonus when they join the team',
      type: 'welcome',
      amount: 5,
      message: '',
    },
    {
      id: '',
      enabled: false,
      name: 'Birthday Bonus',
      description: 'Give employees a special gift on their birthday',
      type: 'birthday',
      amount: 25,
      message: '',
    },
    {
      id: '',
      enabled: false,
      name: 'Work Anniversary Bonus',
      description: 'Celebrate employee work anniversaries',
      type: 'anniversary',
      amount: 50,
      message: '',
    },
  ];

  constructor(
    private ref: MatDialogRef<EditBenefitsDialogComponent, EditBenefitsDialogResult>,
    private teamProgramsService: TeamprogramsService,
    private teamsService: TeamsService,
    private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
    private teamCompaniesService: TeamCompaniesService,
    private stripeService: StripeService,
    private payrollService: PayrollService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: EditBenefitsDialogData,
    private feeService: FeeService,
  ) {
    super();

    this.initSteps(
      [
        { id: 'health', label: 'Health', icon: 'favorite_border' },
        { id: 'supplemental', label: 'Supplemental', icon: 'shield' },
        { id: 'retirement', label: 'Retirement', icon: 'savings' },
        { id: 'pnc', label: 'P&C', icon: 'home' },
        { id: 'other', label: 'Other', icon: 'more_horiz' },
        { id: 'reimbursed', label: 'Reimbursed', icon: 'request_quote' },
        { id: 'bonuses', label: 'Bonuses', icon: 'card_giftcard' },
      ].filter(step => !this.data?.teamManager || ['reimbursed', 'bonuses'].includes(step.id)),
      'tabs',
      0
    );
  }

  ngOnInit(): void {
    if (!this.data?.teamManager) this.loadBenefitProducts();
    this.loadBonuses();
    this.loadReimbursedCategories();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sourceTeamProductLoadVersion++;
    this.missingBenefitProductImageLoadVersion++;
    this.missingBenefitProductImageQueue = [];
    this.missingBenefitProductImageActiveRequests = 0;
  }

  close(): void {
    if (this.isSaving) return;
    this.ref.close({ saved: this.hasPersistedChanges });
  }

  get hasPersistedChanges(): boolean {
    return this.savedSections.size > 0 || this.hasSubmittedGroupProductOnboardingRequest;
  }

  async save(): Promise<void> {
    const teamId = this.data?.teamId;
    if (this.destroyed || this.isSaving || !!this.loadError || this.isLoadingBonuses || this.isLoadingReimbursed || this.isLoadingBenefitsProducts
      || (this.data?.teamManager && (!this.hasLoadedBonuses || !this.hasLoadedReimbursedCategories))) {
      return;
    }

    if (this.reimbursementsEnabled && !this.validatePresetReimbursementCategories()) {
      return;
    }

    if (this.reimbursementsEnabled && !this.validateExcludedTransactionTerms()) {
      return;
    }

    if (this.reimbursementsEnabled && !this.validateIncludedTransactionTerms()) {
      return;
    }

    this.isSaving = true;
    this.saveError = '';
    const previousDisableClose = this.ref.disableClose;
    this.ref.disableClose = true;

    try {
      if (!teamId) {
        this.ref.close({ saved: true });
        return;
      }

      if (this.data?.teamManager) {
        if (this.selectedAllowance !== this.initialAllowance
          && (!Number.isFinite(this.selectedAllowance) || this.selectedAllowance < 0 || this.selectedAllowance > 1000)) {
          this.saveError = 'Enter a monthly allowance between $0 and $1,000.';
          return;
        }
        if (this.initialAllowance !== 0 && this.selectedAllowance !== this.initialAllowance) {
          const confirmed = await this.dialog.open(ConfirmDialogComponent, {
            data: new ConfirmDialogModel('New allowance amount', `You are changing the monthly allowance to $${this.selectedAllowance} per member. <br /> This change will take effect next month. <br /> Any available credit balance will be applied first before charging the remaining amount. <br /> Please confirm.`),
          }).afterClosed().toPromise();
          if (!confirmed || this.destroyed) return;
        }
      }
      // Pause immediately, but keep a paused team paused until its rules have saved.
      if (!this.reimbursementsEnabled) await this.persistReimbursementStatus(teamId);
      if (!this.data?.teamManager) {
        await this.persistBonuses(teamId);
      }
      await this.persistReimbursedCategories(teamId);
      if (this.data?.teamManager) {
        await this.persistBonuses(teamId);
      }
      if (!this.data?.teamManager) await this.persistBenefitProducts(teamId);
      if (this.reimbursementsEnabled) {
        await this.persistCustomReimbursementCategories(teamId);
        await this.persistExcludedTransactionTerms(teamId);
        await this.persistIncludedTransactionTerms(teamId);
        await this.persistReimbursementStatus(teamId);
      }
      this.ref.close({ saved: true });
    } catch (error) {
      this.saveError = this.savedSections.size
        ? `Some changes could not be saved. Already saved: ${Array.from(this.savedSections).join(', ')}. Please retry to finish saving.`
        : 'Your benefits changes could not be saved. Please try again.';
      console.error('Unable to save benefits settings', error);
    } finally {
      this.isSaving = false;
      this.ref.disableClose = previousDisableClose;
    }
  }

  get title(): string {
    const teamName = this.data?.teamName?.trim?.() || '';
    return teamName ? `Add/Edit Benefits for ${teamName} Team` : 'Edit Benefits';
  }

  get subtitle(): string {
    return this.data?.teamManager
      ? 'Manage reimbursement categories, monthly allowance, and bonuses for this team.'
      : 'Select the benefit products and reimbursement categories you want to offer to employees on this team.';
  }

  retryLoading(): void {
    this.loadError = '';
    if (!this.hasLoadedBonuses) void this.loadBonuses();
    if (!this.hasLoadedReimbursedCategories) void this.loadReimbursedCategories();
  }

  onBonusToggle(checked: boolean, setting: BonusSetting): void {
    setting.enabled = checked;
  }

  onAmountInput(setting: BonusSetting, event: Event): void {
    const input = event.target as HTMLInputElement;
    setting.amount = this.toValidMoneyAmount(input.value);
  }

  onReimbursedCategoryClicked(clickedCategory: ReimbursedCategory): void {
    const selectedCount = this.reimbursedCategories.filter(category => category.selected).length;
    const isAtCategoryLimit =
      selectedCount >= this.stripeService.STANDARD_CATEGORIES &&
      !TeamsService.companyPlusPlan &&
      this.currentTeamPlan !== Plan.Plus &&
      this.currentTeamPlan !== Plan.PlusAnnual &&
      !clickedCategory.selected;

    if (isAtCategoryLimit) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `You can select up to ${this.stripeService.STANDARD_CATEGORIES} categories for the standard reimbursement flow.`,
      });
      return;
    }

    clickedCategory.selected = !clickedCategory.selected;
  }

  get taxableReimbursedCategories(): ReimbursedCategory[] {
    return this.sortReimbursedCategories(this.reimbursedCategories.filter(category => category.taxable));
  }

  get nonTaxableReimbursedCategories(): ReimbursedCategory[] {
    return this.sortReimbursedCategories(this.reimbursedCategories.filter(category => !category.taxable));
  }

  get activeCustomReimbursementCategories(): CustomReimbursementCategory[] {
    return [...this.customReimbursementCategories]
      .filter((category) => !category.archived && !this.isPresetBackedCustomReimbursementCategory(category))
      .sort((a, b) => this.compareAlphabetically(a.name, b.name));
  }

  get isCustomReimbursementEditorVisible(): boolean {
    return this.isCreatingCustomReimbursementCategory || !!this.editingCustomReimbursementCategoryKey;
  }

  get presetReimbursementCategories(): PresetReimbursementCategoryState[] {
    return this.reimbursementCategoryPresets.map((preset) => {
      const category = this.getActivePresetReimbursementCategory(preset.key);
      return {
        preset,
        category,
        enabled: !!category,
      };
    });
  }

  get broadExcludedTransactionTerms(): string[] {
    return this.excludedTransactionTerms.filter((term) =>
      this.broadExcludedTransactionTermSet.has(term.trim().toLowerCase())
    );
  }

  get assignableReimbursementProducts(): AssignableBenefitProductItem[] {
    if (!this.selectedBenefitProductIds.size) {
      return [];
    }

    const selectedIds = this.payrollBenefitsAssignmentService.getPersistedSelectedProductIds(
      Array.from(this.selectedBenefitProductIds),
      this.companyProductIdsInOrder
    );
    return selectedIds
      .map((productId) => this.reimbursementProductLookup.get(productId) || this.toFallbackAssignableProduct(productId))
      .sort((a, b) => this.compareAlphabetically(a.label, b.label));
  }

  get canShowSourceTeamProductHelper(): boolean {
    if (!this.hasLoadedBenefitProducts || !this.data?.teamId) {
      return false;
    }

    const selectedProductIds = new Set([
      ...this.selectedBenefitProductIds,
      ...this.preservedNativeProductIds,
    ]);
    const hasSelectedConfigurableProduct = Array.from(selectedProductIds).some((productId) => {
      const product = this.benefitProducts.find((candidate) => candidate.id === productId);
      return product?.alwaysActive !== true && !this.alwaysActiveProductIds.has(productId);
    });

    return !hasSelectedConfigurableProduct;
  }

  override getStepBadgeCount(step: StepDefinition): number | null {
    switch (step.id) {
      case 'health':
      case 'supplemental':
      case 'retirement':
      case 'pnc':
      case 'other':
        return this.getSelectedBenefitCountForCategory(step.id);
      default:
        return null;
    }
  }

  getBenefitSections(category: BenefitCategoryTab): BenefitSection[] {
    const products = this.benefitProducts.filter(product => product.mapping.category === category);
    const sectionDefs: PayrollBenefitSectionDefinition[] = buildPayrollBenefitSectionDefinitions(
      category,
      this.subcategoryDefinitionsByCategory,
      products.map((product) => product.mapping)
    );

    const sections: BenefitSection[] = sectionDefs.map(section => ({
      id: section.id,
      title: section.title,
      products: this.sortBenefitProducts(
        section.subcategory
          ? products.filter(product => product.mapping.subcategory === section.subcategory)
          : [...products]
      ),
    }));

    const assignedIds = new Set(
      sections.reduce((all: string[], section: BenefitSection) => {
        return all.concat(section.products.map((product: BenefitProductItem) => product.id));
      }, [])
    );
    const uncategorizedProducts = products.filter(product => !assignedIds.has(product.id));
    if (uncategorizedProducts.length > 0 || sections.length === 0) {
      sections.push({
        id: 'general',
        title: 'General',
        products: this.sortBenefitProducts(uncategorizedProducts),
      });
    }

    return sections.sort((a, b) => {
      const aHasProducts = a.products.length > 0;
      const bHasProducts = b.products.length > 0;
      if (aHasProducts === bHasProducts) {
        return 0;
      }
      return aHasProducts ? -1 : 1;
    });
  }

  isBenefitProductSelected(productId: string): boolean {
    return this.selectedBenefitProductIds.has(productId);
  }

  isBenefitProductPending(product: BenefitProductItem): boolean {
    return product?.approvalStatus === 'pending';
  }

  isBenefitProductRequestable(product: BenefitProductItem): boolean {
    return product?.requiresApproval === true && product?.canRequestApproval === true;
  }

  canToggleBenefitProduct(product: BenefitProductItem): boolean {
    return !this.isSaving
      && product?.alwaysActive !== true
      && !this.pendingOnboardingRequestProductIds.has(product.id)
      && !this.isBenefitProductPending(product);
  }

  canInteractWithBenefitProductCheckbox(product: BenefitProductItem): boolean {
    if (this.isThatchSetupBlocked(product)) return false;
    return this.canToggleBenefitProduct(product) && !this.isBenefitProductRequestable(product);
  }

  get isPayrollDeductionAvailable(): boolean {
    return this.isPayrollDeductionFeatureEnabled
      && this.payrollService.providerConnectionSupportsCapability(
        this.payrollProviderConnection,
        'supportsPayrollDeductionSetup',
      );
  }

  canEditPayrollDeduction(): boolean {
    return !this.isSaving && this.isPayrollDeductionAvailable;
  }

  async onPayrollDeductionToggle(product: BenefitProductItem, checked: boolean): Promise<void> {
    if (!this.canEditPayrollDeduction()) {
      return;
    }

    product.payrollDeductionEnabled = checked;
    if (!checked) {
      product.payrollDeductionCode = '';
      product.payrollDeductionCodeDescription = '';
      return;
    }

    await this.ensurePayrollDeductionCodesLoaded();
  }

  onPayrollDeductionCodeSelected(product: BenefitProductItem, deductionCode: string): void {
    const normalizedDeductionCode = `${deductionCode || ''}`.trim();
    const matchedCode = this.payrollDeductionCodes.find((option) => option.code === normalizedDeductionCode);
    product.payrollDeductionCode = normalizedDeductionCode;
    product.payrollDeductionCodeDescription = matchedCode?.description || '';
  }

  async retryPayrollDeductionCodesLoad(): Promise<void> {
    await this.ensurePayrollDeductionCodesLoaded(true);
  }

  getPayrollDeductionValidationMessage(product: BenefitProductItem): string {
    if (product?.payrollDeductionEnabled !== true || !this.isPayrollDeductionAvailable) {
      return '';
    }

    if (this.isLoadingPayrollDeductionCodes) {
      return 'Loading deduction codes from UKG Ready...';
    }

    if (this.payrollDeductionCodesLoadError) {
      return `${this.payrollDeductionCodesLoadError} Add or manage the deduction code in UKG Ready, then retry.`;
    }

    if (this.hasAttemptedPayrollDeductionCodesLoad && this.payrollDeductionCodes.length === 0) {
      return 'No deduction codes are available from UKG Ready yet. Add the code in UKG Ready, then retry.';
    }

    const selectedCode = `${product?.payrollDeductionCode || ''}`.trim();
    if (!selectedCode) {
      return 'Select a deduction code before saving. If the desired deduction code is not listed, add it in UKG Ready.';
    }

    if (!this.payrollDeductionCodes.some((option) => option.code === selectedCode)) {
      return 'This deduction code is not currently available from UKG Ready. Add or manage it in UKG Ready, then retry.';
    }

    return '';
  }

  getBenefitProductStatusLabel(product: BenefitProductItem): string {
    if (product.vendorSlug === 'thatch') {
      if (this.thatchSetup?.method === 'employer_confirmed') return 'Employer-confirmed';
      if (this.thatchSetup?.method === 'existing_access') return 'Existing access preserved';
      if (this.isThatchSetupBlocked(product)) return this.thatchSetup ? 'Employer setup required' : 'Employer setup unavailable — open setup to retry';
    }
    if (this.pendingOnboardingRequestProductIds.has(product.id)) {
      return 'Sending request...';
    }
    return product?.approvalLabel || '';
  }

  getBenefitProductStatusClass(product: BenefitProductItem): string {
    if (this.pendingOnboardingRequestProductIds.has(product.id)) {
      return 'status-pending';
    }

    const label = `${product?.approvalLabel || ''}`.trim().toLowerCase();
    if (label === 'ready') {
      return 'status-available';
    }

    if (label) {
      return 'status-action-needed';
    }

    return '';
  }

  onBenefitProductToggle(productId: string, checked: boolean): void {
    const product = this.benefitProducts.find(p => p.id === productId);
    if (checked && product && this.isThatchSetupBlocked(product)) return;
    if (this.isSaving) {
      return;
    }
    const next = new Set(this.selectedBenefitProductIds);
    if (checked) {
      next.add(productId);
    } else {
      next.delete(productId);
    }
    this.selectedBenefitProductIds = next;
    this.sourceTeamProductPreviewApplied = false;
  }

  canShowAllowanceControl(product: BenefitProductItem): boolean {
    return product?.allowanceControlEnabled === true
      && (this.isBenefitProductSelected(product.id) || product?.alwaysActive === true);
  }

  onAllowanceEligibilityToggle(product: BenefitProductItem, checked: boolean): void {
    if (this.isSaving || !this.canShowAllowanceControl(product)) {
      return;
    }
    product.allowanceEligible = checked;
    this.sourceTeamProductPreviewApplied = false;
  }

  async loadSourceTeamProductPreview(): Promise<void> {
    this.resetSourceTeamProductPreview();
    const sourceTeamId = `${this.selectedSourceTeamId || ''}`.trim();
    if (!sourceTeamId) {
      this.sourceTeamProductPreviewError = 'Choose a team first.';
      return;
    }
    if (sourceTeamId === this.data?.teamId) {
      this.sourceTeamProductPreviewError = 'Choose a different team.';
      return;
    }

    const loadVersion = ++this.sourceTeamProductLoadVersion;
    this.isSourceTeamProductPreviewLoading = true;
    try {
      // The endpoint authorizes this source team; the selector is only a convenience list.
      const selection = await this.teamsService.getCompanyScopedTeamProductSelection(
        this.data.companyId!,
        sourceTeamId,
      );
      if (loadVersion !== this.sourceTeamProductLoadVersion || sourceTeamId !== this.selectedSourceTeamId) {
        return;
      }
      this.buildSourceTeamProductPreview(selection);
    } catch (error) {
      if (loadVersion === this.sourceTeamProductLoadVersion && sourceTeamId === this.selectedSourceTeamId) {
        this.sourceTeamProductPreviewError = this.getErrorMessage(error);
      }
    } finally {
      if (loadVersion === this.sourceTeamProductLoadVersion) {
        this.isSourceTeamProductPreviewLoading = false;
      }
    }
  }

  onSourceTeamSelectionChanged(): void {
    this.resetSourceTeamProductPreview();
    this.isSourceTeamProductPreviewLoading = false;
  }

  canApplySourceTeamProductPreview(): boolean {
    return this.sourceTeamProductPreviewProductIds.length > 0 && !this.isSourceTeamProductPreviewLoading;
  }

  applySourceTeamProductPreview(): void {
    if (!this.canApplySourceTeamProductPreview()) {
      return;
    }

    if (this.benefitProducts.some(p => this.isThatchSetupBlocked(p) && this.sourceTeamProductPreviewProductIds.includes(p.id))) {
      this.sourceTeamProductPreviewError = 'Confirm employer setup in Thatch before copying a selection that includes it.';
      return;
    }
    const nextSelectedIds = new Set(this.sourceTeamProductPreviewProductIds);
    const nextAllowanceEligibleIds = new Set(this.sourceTeamProductPreviewAllowanceEligibleProductIds);
    this.payrollBenefitsAssignmentService.getAlwaysActiveBenefitProductIds(this.benefitProducts)
      .forEach((productId) => nextSelectedIds.add(productId));
    this.selectedBenefitProductIds = nextSelectedIds;
    this.benefitProducts = this.benefitProducts.map((product) => ({
      ...product,
      allowanceEligible: product.allowanceControlEnabled === false
        ? product.allowanceEligible
        : nextAllowanceEligibleIds.has(product.id),
    }));
    this.sourceTeamProductPreviewApplied = true;
  }

  getSourceTeamProductDisplay(productId: string): string {
    const product = this.benefitProducts.find((candidate) => candidate.id === productId);
    return product
      ? [product.vendorName, product.productName].filter(Boolean).join(' - ') || productId
      : productId;
  }

  toggleBenefitProduct(productId: string): void {
    this.onBenefitProductToggle(productId, !this.isBenefitProductSelected(productId));
  }

  async onBenefitProductClicked(product: BenefitProductItem): Promise<void> {
    if (this.isSaving || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }
    if (product?.alwaysActive === true) {
      return;
    }
    if (this.isBenefitProductPending(product)) {
      return;
    }
    if (this.isBenefitProductRequestable(product)) {
      await this.requestGroupProductOnboarding(product);
      return;
    }
    this.toggleBenefitProduct(product.id);
  }

  async onBenefitProductCheckboxChanged(product: BenefitProductItem, checked: boolean): Promise<void> {
    if (this.isSaving || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }
    if (product?.alwaysActive === true) {
      return;
    }
    if (this.isBenefitProductRequestable(product)) {
      return;
    }
    if (this.isBenefitProductPending(product)) {
      return;
    }
    this.onBenefitProductToggle(product.id, checked);
  }

  openThatchHelp(event: Event): void {
    event.stopPropagation();
    this.dialog.open(ThatchHelpDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      ariaLabelledBy: 'thatch-help-title',
      data: {companyId: this.data?.companyId},
    }).afterClosed().subscribe((setup: ThatchEmployerSetup | undefined) => {
      if (setup && setup.companyId === this.data?.companyId) this.thatchSetup = setup;
    });
  }

  openBenefitProductDetails(product: BenefitProductItem, event: Event): void {
    event.stopPropagation();
    const normalizedUrl = `${product?.detailsUrl || ''}`.trim();
    if (!normalizedUrl) {
      return;
    }
    window.open(normalizedUrl, '_blank', 'noopener');
  }

  trackBenefitSection(index: number, section: BenefitSection): string {
    return section.id;
  }

  trackBenefitProduct(index: number, product: BenefitProductItem): string {
    return product.id;
  }

  trackCustomReimbursementCategory(index: number, category: CustomReimbursementCategory): string {
    return category.clientKey;
  }

  trackPresetReimbursementCategory(index: number, item: PresetReimbursementCategoryState): string {
    return item.preset.key;
  }

  trackAssignableReimbursementProduct(index: number, product: AssignableBenefitProductItem): string {
    return product.id;
  }

  getAssignableReimbursementProductLabel(productId: string): string {
    const product = this.reimbursementProductLookup.get(productId);
    return product?.label || productId;
  }

  isCustomReimbursementDraftSpendCategorySelected(categoryName: string): boolean {
    return this.customReimbursementDraft.spendCategories.includes(categoryName);
  }

  isCustomReimbursementDraftProductSelected(productId: string): boolean {
    return this.customReimbursementDraft.vendorProductIds.includes(productId);
  }

  onCustomReimbursementDraftSpendCategoryClicked(categoryName: string): void {
    const selected = new Set(this.customReimbursementDraft.spendCategories);
    if (selected.has(categoryName)) {
      selected.delete(categoryName);
    } else {
      selected.add(categoryName);
    }
    this.customReimbursementDraft = {
      ...this.customReimbursementDraft,
      spendCategories: Array.from(selected),
    };
  }

  onCustomReimbursementDraftProductClicked(productId: string): void {
    const selected = new Set(this.customReimbursementDraft.vendorProductIds);
    if (selected.has(productId)) {
      selected.delete(productId);
    } else {
      selected.add(productId);
    }
    this.customReimbursementDraft = {
      ...this.customReimbursementDraft,
      vendorProductIds: Array.from(selected),
    };
  }

  onCustomReimbursementDraftNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.customReimbursementDraft = {
      ...this.customReimbursementDraft,
      name: (input.value || '').trimStart(),
    };
  }

  onCustomReimbursementDraftAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.customReimbursementDraft = {
      ...this.customReimbursementDraft,
      maxAmountDollars: this.toValidMoneyAmount(input.value),
    };
  }

  onCustomReimbursementDraftRolloverToggle(checked: boolean): void {
    this.customReimbursementDraft = {
      ...this.customReimbursementDraft,
      rollover: checked,
    };
  }

  addExcludedTransactionTerm(event: MatChipInputEvent): void {
    this.upsertExcludedTransactionTerm(event.value);
    event.chipInput?.clear();
  }

  addIncludedTransactionTerm(event: MatChipInputEvent): void {
    this.upsertIncludedTransactionTerm(event.value);
    event.chipInput?.clear();
  }

  removeExcludedTransactionTerm(term: string): void {
    const normalizedTerm = `${term || ''}`.trim().toLowerCase();
    this.excludedTransactionTerms = this.excludedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm);
  }

  removeIncludedTransactionTerm(term: string): void {
    const normalizedTerm = `${term || ''}`.trim().toLowerCase();
    this.includedTransactionTerms = this.includedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm);
  }

  trackExcludedTransactionTerm(index: number, term: string): string {
    return `${term}`.trim().toLowerCase();
  }

  trackIncludedTransactionTerm(index: number, term: string): string {
    return `${term}`.trim().toLowerCase();
  }

  async onPresetReimbursementCategoryToggle(
    presetKey: ReimbursementCategoryPresetKey,
    checked: boolean,
  ): Promise<void> {
    if (checked) {
      this.enablePresetReimbursementCategory(presetKey);
      return;
    }

    const category = this.getActivePresetReimbursementCategory(presetKey);
    if (!category) {
      return;
    }

    await this.archiveCustomReimbursementCategory(category);
  }

  onPresetReimbursementCategoryAmountInput(
    presetKey: ReimbursementCategoryPresetKey,
    event: Event,
  ): void {
    const input = event.target as HTMLInputElement;
    this.upsertPresetReimbursementCategory(presetKey, {
      maxAmountCents: Math.round(this.toValidMoneyAmount(input.value) * 100),
    });
  }

  onPresetReimbursementCategoryRolloverToggle(
    presetKey: ReimbursementCategoryPresetKey,
    checked: boolean,
  ): void {
    this.upsertPresetReimbursementCategory(presetKey, {
      rollover: checked,
    });
  }

  startCustomReimbursementCategory(): void {
    this.isCreatingCustomReimbursementCategory = true;
    this.editingCustomReimbursementCategoryKey = null;
    this.customReimbursementDraft = this.createEmptyCustomReimbursementDraft();
  }

  editCustomReimbursementCategory(category: CustomReimbursementCategory): void {
    this.isCreatingCustomReimbursementCategory = false;
    this.editingCustomReimbursementCategoryKey = category.clientKey;
    this.customReimbursementDraft = {
      name: category.name,
      maxAmountDollars: category.maxAmountCents / 100,
      rollover: category.rollover,
      spendCategories: [...category.spendCategories],
      vendorProductIds: [...category.vendorProductIds],
    };
  }

  cancelCustomReimbursementEdit(): void {
    this.isCreatingCustomReimbursementCategory = false;
    this.editingCustomReimbursementCategoryKey = null;
    this.customReimbursementDraft = this.createEmptyCustomReimbursementDraft();
  }

  async archiveCustomReimbursementCategory(category: CustomReimbursementCategory): Promise<void> {
    if (!category.id) {
      this.customReimbursementCategories = this.customReimbursementCategories
        .filter((item) => item.clientKey !== category.clientKey);
      if (this.editingCustomReimbursementCategoryKey === category.clientKey) {
        this.cancelCustomReimbursementEdit();
      }
      return;
    }

    const targetCategories = this.customReimbursementCategories
      .filter((item) => !item.archived && item.id && item.id !== category.id)
      .map((item) => this.normalizePresetBackedCustomReimbursementCategory(item))
      .map((item) => ({ id: item.id as string, name: item.name }));

    const dialogRef = this.dialog.open(
      CustomReimbursementRolloverResolutionDialogComponent,
      {
        data: {
          sourceCategoryName: category.name,
          targetCategories,
        },
        disableClose: true,
        width: '560px',
      }
    );

    const resolution = await dialogRef.afterClosed().toPromise();
    if (!resolution) {
      return;
    }

    this.upsertPendingCustomReimbursementCategoryResolution(category.id, resolution);
    this.customReimbursementCategories = this.customReimbursementCategories.map((item) => {
      if (item.clientKey !== category.clientKey) {
        return item;
      }
      return {
        ...item,
        archived: true,
        updatedAt: new Date().toISOString(),
      };
    });
    if (this.editingCustomReimbursementCategoryKey === category.clientKey) {
      this.cancelCustomReimbursementEdit();
    }
  }

  private upsertPendingCustomReimbursementCategoryResolution(
    sourceCategoryId: string,
    resolution: CustomReimbursementRolloverResolutionDialogResult
  ): void {
    const nextResolution: CustomReimbursementCategoryResolution = {
      sourceCategoryId,
      action: resolution.action,
      ...(resolution.targetCategoryId ? { targetCategoryId: resolution.targetCategoryId } : {}),
    };

    this.pendingCustomReimbursementCategoryResolutions =
      this.pendingCustomReimbursementCategoryResolutions
        .filter((item) => item.sourceCategoryId !== sourceCategoryId)
        .concat(nextResolution);
  }

  saveCustomReimbursementCategory(): void {
    const normalizedName = (this.customReimbursementDraft.name || '').trim();
    const selectedSpendCategories = [...new Set(this.customReimbursementDraft.spendCategories)];
    const selectedProductIds = [...new Set(this.customReimbursementDraft.vendorProductIds)];
    const maxAmountCents = Math.round(this.toValidMoneyAmount(this.customReimbursementDraft.maxAmountDollars) * 100);

    if (!normalizedName) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Enter a category name.',
      });
      return;
    }
    if (!selectedSpendCategories.length && !selectedProductIds.length) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Select at least one spend category.',
      });
      return;
    }
    if (maxAmountCents <= 0) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Max Amount must be greater than $0.',
      });
      return;
    }

    const now = new Date().toISOString();
    if (this.editingCustomReimbursementCategoryKey) {
      this.customReimbursementCategories = this.customReimbursementCategories.map((category) => {
        if (category.clientKey !== this.editingCustomReimbursementCategoryKey) {
          return category;
        }
        return {
          ...category,
          presetKey: undefined,
          name: normalizedName,
          spendCategories: selectedSpendCategories,
          vendorProductIds: selectedProductIds,
          maxAmountCents,
          frequency: 'monthly',
          rollover: !!this.customReimbursementDraft.rollover,
          archived: false,
          updatedAt: now,
        };
      });
    } else {
      this.customReimbursementCategories = [
        ...this.customReimbursementCategories,
        {
          clientKey: this.generateCustomReimbursementCategoryClientKey(),
          presetKey: undefined,
          name: normalizedName,
          spendCategories: selectedSpendCategories,
          vendorProductIds: selectedProductIds,
          maxAmountCents,
          frequency: 'monthly',
          rollover: !!this.customReimbursementDraft.rollover,
          archived: false,
          createdAt: now,
          updatedAt: now,
        },
      ];
    }

    this.cancelCustomReimbursementEdit();
  }

  private validatePresetReimbursementCategories(): boolean {
    const invalidPreset = this.presetReimbursementCategories.find((item) =>
      item.enabled && (item.category?.maxAmountCents || 0) <= 0
    );
    if (!invalidPreset) {
      return true;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: `${invalidPreset.preset.label} monthly max amount must be greater than $0.`,
    });
    return false;
  }

  private validateExcludedTransactionTerms(): boolean {
    const invalidTerm = this.excludedTransactionTerms
      .map((term) => `${term || ''}`.trim())
      .find((term) => term.replace(/\s/g, '').length < 2);
    if (!invalidTerm) {
      return true;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: 'Excluded transaction names must have at least two non-space characters.',
    });
    return false;
  }

  private validateIncludedTransactionTerms(): boolean {
    const invalidTerm = this.includedTransactionTerms
      .map((term) => `${term || ''}`.trim())
      .find((term) => term.replace(/\s/g, '').length < 2);
    if (!invalidTerm) {
      return true;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: 'Included transaction names must have at least two non-space characters.',
    });
    return false;
  }

  private enablePresetReimbursementCategory(presetKey: ReimbursementCategoryPresetKey): void {
    if (this.getActivePresetReimbursementCategory(presetKey)) {
      return;
    }

    const pendingRestoreCategory = this.getPendingArchivedPresetReimbursementCategory(presetKey);
    const preset = this.getReimbursementCategoryPreset(presetKey);
    if (!preset) {
      return;
    }

    if (pendingRestoreCategory) {
      this.removePendingCustomReimbursementCategoryResolution(pendingRestoreCategory.id);
      const now = new Date().toISOString();
      this.customReimbursementCategories = this.customReimbursementCategories.map((category) => {
        if (category.clientKey !== pendingRestoreCategory.clientKey) {
          return category;
        }
        return {
          ...category,
          presetKey: preset.key,
          name: preset.label,
          spendCategories: [...preset.spendCategories],
          vendorProductIds: [],
          frequency: 'monthly',
          archived: false,
          updatedAt: now,
        };
      });
      return;
    }

    this.customReimbursementCategories = this.customReimbursementCategories.concat(
      this.createPresetReimbursementCategory(preset)
    );
  }

  private upsertPresetReimbursementCategory(
    presetKey: ReimbursementCategoryPresetKey,
    updates: Partial<Pick<CustomReimbursementCategory, 'maxAmountCents' | 'rollover'>>,
  ): void {
    const existingCategory = this.getActivePresetReimbursementCategory(presetKey);
    if (!existingCategory) {
      this.enablePresetReimbursementCategory(presetKey);
    }

    const preset = this.getReimbursementCategoryPreset(presetKey);
    const targetCategory = this.getActivePresetReimbursementCategory(presetKey);
    if (!preset || !targetCategory) {
      return;
    }

    const now = new Date().toISOString();
    this.customReimbursementCategories = this.customReimbursementCategories.map((category) => {
      if (category.clientKey !== targetCategory.clientKey) {
        return category;
      }
      return {
        ...category,
        presetKey: preset.key,
        name: preset.label,
        spendCategories: [...preset.spendCategories],
        vendorProductIds: [],
        frequency: 'monthly',
        archived: false,
        updatedAt: now,
        ...updates,
      };
    });
  }

  private getReimbursementCategoryPreset(
    presetKey: ReimbursementCategoryPresetKey | undefined | null,
  ): ReimbursementCategoryPresetDefinition | null {
    if (!presetKey) {
      return null;
    }
    return this.reimbursementCategoryPresetLookup.get(presetKey) || null;
  }

  private getPresetDefinitionForCategory(
    category: CustomReimbursementCategory | null | undefined,
  ): ReimbursementCategoryPresetDefinition | null {
    if (!category) {
      return null;
    }

    const keyedPreset = this.getReimbursementCategoryPreset(category.presetKey);
    if (keyedPreset) {
      return keyedPreset;
    }

    if ((category.vendorProductIds || []).length > 0) {
      return null;
    }

    return this.reimbursementCategoryPresets.find((preset) =>
      this.hasMatchingSpendCategorySet(category.spendCategories, preset.spendCategories)
    ) || null;
  }

  private isPresetBackedCustomReimbursementCategory(category: CustomReimbursementCategory): boolean {
    return !!this.getPresetDefinitionForCategory(category);
  }

  private getActivePresetReimbursementCategory(
    presetKey: ReimbursementCategoryPresetKey,
  ): CustomReimbursementCategory | null {
    const activeCategories = this.customReimbursementCategories.filter((category) => !category.archived);
    const directMatch = activeCategories.find((category) => category.presetKey === presetKey);
    if (directMatch) {
      return directMatch;
    }

    return activeCategories.find((category) => {
      const preset = this.getPresetDefinitionForCategory(category);
      return preset?.key === presetKey;
    }) || null;
  }

  private getPendingArchivedPresetReimbursementCategory(
    presetKey: ReimbursementCategoryPresetKey,
  ): CustomReimbursementCategory | null {
    return this.customReimbursementCategories.find((category) => {
      if (!category.archived || !category.id) {
        return false;
      }

      const preset = this.getPresetDefinitionForCategory(category);
      if (preset?.key !== presetKey) {
        return false;
      }

      return this.pendingCustomReimbursementCategoryResolutions
        .some((resolution) => resolution.sourceCategoryId === category.id);
    }) || null;
  }

  private removePendingCustomReimbursementCategoryResolution(sourceCategoryId: string | undefined): void {
    if (!sourceCategoryId) {
      return;
    }

    this.pendingCustomReimbursementCategoryResolutions =
      this.pendingCustomReimbursementCategoryResolutions
        .filter((item) => item.sourceCategoryId !== sourceCategoryId);
  }

  private hasMatchingSpendCategorySet(left: string[], right: string[]): boolean {
    const leftSet = [...new Set((left || []).map((value) => `${value}`.trim()).filter(Boolean))].sort();
    const rightSet = [...new Set((right || []).map((value) => `${value}`.trim()).filter(Boolean))].sort();

    if (leftSet.length !== rightSet.length) {
      return false;
    }

    return leftSet.every((value, index) => value === rightSet[index]);
  }

  private createPresetReimbursementCategory(
    preset: ReimbursementCategoryPresetDefinition,
  ): CustomReimbursementCategory {
    const now = new Date().toISOString();
    return {
      clientKey: this.generateCustomReimbursementCategoryClientKey(),
      presetKey: preset.key,
      name: preset.label,
      spendCategories: [...preset.spendCategories],
      vendorProductIds: [],
      maxAmountCents: 0,
      frequency: 'monthly',
      rollover: false,
      archived: false,
      createdAt: now,
      updatedAt: now,
    };
  }

  private normalizePresetBackedCustomReimbursementCategory(
    category: CustomReimbursementCategory,
  ): CustomReimbursementCategory {
    const preset = this.getPresetDefinitionForCategory(category);
    if (!preset) {
      return {
        ...category,
      };
    }

    return {
      ...category,
      presetKey: preset.key,
      name: preset.label,
      spendCategories: [...preset.spendCategories],
      vendorProductIds: [],
      frequency: 'monthly',
    };
  }

  private async loadProductSourceTeams(): Promise<void> {
    this.sourceTeamOptions = [];
    this.selectedSourceTeamId = '';
    this.sourceTeamOptionsError = '';
    this.resetSourceTeamProductPreview();
    if (!this.data?.teamId || !this.data?.companyId) {
      return;
    }

    this.isSourceTeamOptionsLoading = true;
    try {
      const teams = await this.getAvailableProductSourceTeams();
      this.sourceTeamOptions = this.normalizeSourceTeamOptions(teams);
    } catch (error) {
      this.sourceTeamOptionsError = this.getErrorMessage(error);
    } finally {
      this.isSourceTeamOptionsLoading = false;
    }
  }

  private async getAvailableProductSourceTeams(): Promise<any[]> {
    try {
      return await this.teamsService.getTeamsForCompany(this.data.companyId!);
    } catch (error) {
      if (this.sessionService.isSuperAdmin()) {
        throw error;
      }
    }

    const userId = this.sessionService.getUserId();
    return userId ? this.teamsService.getUserTeamsInfo(userId) : [];
  }

  private normalizeSourceTeamOptions(teams: any[]): BenefitProductSourceTeamOption[] {
    const seenIds = new Set<string>();
    return (Array.isArray(teams) ? teams : [])
      .reduce((options: BenefitProductSourceTeamOption[], team) => {
        const id = `${team?.id || ''}`.trim();
        if (!id || id === this.data?.teamId || seenIds.has(id)) {
          return options;
        }
        seenIds.add(id);
        options.push({ id, label: `${team?.name || id}`.trim() });
        return options;
      }, [])
      .sort((left, right) => left.label.localeCompare(right.label, undefined, {
        numeric: true,
        sensitivity: 'base',
      }));
  }

  private buildSourceTeamProductPreview(selection: ManageWellspaceProductSelection): void {
    const sourceProductIds = this.getUniqueProductIds(selection?.selectedProductIds || []);
    const availableIds = new Set(this.companyProductIdsInOrder);
    const editableIds = new Set(this.benefitProducts.map((product) => product.id));
    const availableSourceProductIds = sourceProductIds.filter((id) => availableIds.has(id));
    const editableSourceProductIds = availableSourceProductIds.filter((id) => editableIds.has(id));
    const matchingIds = editableSourceProductIds.filter((id) => this.canApplySourceTeamProduct(id));
    const skippedIds = [
      ...sourceProductIds.filter((id) => !availableIds.has(id)),
      ...editableSourceProductIds.filter((id) => !this.canApplySourceTeamProduct(id)),
    ];
    const hiddenIds = availableSourceProductIds.filter((id) => !editableIds.has(id));
    const alwaysActiveIds = this.payrollBenefitsAssignmentService.getAlwaysActiveBenefitProductIds(this.benefitProducts);
    const nextEffectiveIds = new Set([...matchingIds, ...alwaysActiveIds]);
    const currentIds = new Set(this.selectedBenefitProductIds);

    this.sourceTeamProductPreviewProductIds = this.normalizeEditableProductIds(matchingIds);
    this.sourceTeamProductPreviewAddedProductIds = this.normalizeEditableProductIds(
      Array.from(nextEffectiveIds).filter((id) => !currentIds.has(id))
    );
    this.sourceTeamProductPreviewOverlappingProductIds = this.normalizeEditableProductIds(
      Array.from(nextEffectiveIds).filter((id) => currentIds.has(id))
    );
    this.sourceTeamProductPreviewRemovedProductIds = this.normalizeEditableProductIds(
      Array.from(currentIds).filter((id) => !nextEffectiveIds.has(id))
    );
    this.sourceTeamProductPreviewSkippedProductIds = skippedIds;
    this.sourceTeamProductPreviewHiddenProductIds = hiddenIds;
    this.sourceTeamProductPreviewAllowanceEligibleProductIds = this.normalizeEditableProductIds(
      this.getUniqueProductIds(selection?.allowanceEligibleProductIds || [])
        .filter((id) => nextEffectiveIds.has(id))
        .filter((id) => this.benefitProducts.find((product) => product.id === id)?.allowanceControlEnabled !== false)
    );
  }

  private resetSourceTeamProductPreview(): void {
    this.sourceTeamProductLoadVersion++;
    this.isSourceTeamProductPreviewLoading = false;
    this.sourceTeamProductPreviewError = '';
    this.sourceTeamProductPreviewProductIds = [];
    this.sourceTeamProductPreviewAddedProductIds = [];
    this.sourceTeamProductPreviewOverlappingProductIds = [];
    this.sourceTeamProductPreviewRemovedProductIds = [];
    this.sourceTeamProductPreviewSkippedProductIds = [];
    this.sourceTeamProductPreviewHiddenProductIds = [];
    this.sourceTeamProductPreviewAllowanceEligibleProductIds = [];
    this.sourceTeamProductPreviewApplied = false;
  }

  private getUniqueProductIds(productIds: string[]): string[] {
    return Array.from(new Set((productIds || []).map((id) => `${id || ''}`.trim()).filter(Boolean)));
  }

  private normalizeEditableProductIds(productIds: string[]): string[] {
    const productIdSet = new Set(this.getUniqueProductIds(productIds));
    return this.benefitProducts.map((product) => product.id).filter((id) => productIdSet.has(id));
  }

  private canApplySourceTeamProduct(productId: string): boolean {
    const product = this.benefitProducts.find((candidate) => candidate.id === productId);
    return !!product && (
      product.alwaysActive === true
      || (!this.isBenefitProductPending(product) && !this.isBenefitProductRequestable(product))
    );
  }

  private getSelectedBenefitCountForCategory(category: BenefitCategoryTab): number | null {
    const count = this.benefitProducts.reduce((total: number, product: BenefitProductItem) => {
      if (product.mapping.category !== category || !this.selectedBenefitProductIds.has(product.id)) {
        return total;
      }
      return total + 1;
    }, 0);

    return count > 0 ? count : null;
  }

  private async loadBenefitProducts(): Promise<void> {
    if (this.data?.teamManager) return;
    const companyId = this.data?.companyId;
    if (!companyId) {
      return;
    }

    this.isLoadingBenefitsProducts = true;
    const isNativeCompanyManager = this.data?.workspaceContext === 'nativeCompanyManager';
    const payrollDeductionFeatureFlagPromise = isNativeCompanyManager
      ? Promise.resolve().then(() => {
        this.isPayrollDeductionFeatureEnabled = false;
      })
      : this.loadPayrollDeductionFeatureFlag();

    try {
      const teamId = this.data?.teamId;
      const benefitsLoad: BenefitProductsLoadResult =
        await this.payrollBenefitsAssignmentService.loadBenefitProductsForCompany(companyId, teamId, {
          includeStripeProductEnrichment: false,
          company: isNativeCompanyManager ? this.data?.company : undefined,
          includeGroupProductEnrichment: !isNativeCompanyManager,
        });
      this.payrollProviderConnection = await this.payrollService
        .getCompanyPayrollProviderConnection(companyId)
        .catch(() => null);
      this.companyInfo = benefitsLoad.company;
      this.teamInfo = benefitsLoad.team;
      this.alwaysActiveProductIds = new Set(
        this.teamCompaniesService.getAlwaysActiveProductIds(this.companyInfo)
      );
      this.subcategoryDefinitionsByCategory =
        this.teamCompaniesService.getResolvedPayrollConfig(this.companyInfo)?.payrollBenefitSubcategoryDefinitionsByCategory || {};
      this.payrollDeductionCodes = [];
      this.payrollDeductionCodesLoadError = '';
      this.hasAttemptedPayrollDeductionCodesLoad = false;
      this.payrollDeductionCodesRequest = undefined;
      const availableBenefitProductIds = isNativeCompanyManager
        ? new Set((this.data?.availableBenefitProductIds || []).map((id) => `${id || ''}`.trim()).filter(Boolean))
        : null;
      this.companyProductIdsInOrder = [...benefitsLoad.companyProductIdsInOrder];
      this.benefitProducts = benefitsLoad.benefitProducts.filter((product) => {
        return !availableBenefitProductIds || availableBenefitProductIds.has(product.id);
      });
      const editableBenefitProductIds = new Set(this.benefitProducts.map((product) => product.id));
      this.preservedNativeProductIds = benefitsLoad.baselineSelectedIds.filter((id) => {
        return this.companyProductIdsInOrder.includes(id) && !editableBenefitProductIds.has(id);
      });
      const selectedSet = new Set(
        benefitsLoad.baselineSelectedIds.filter((id: string) => editableBenefitProductIds.has(id))
      );
      this.selectedBenefitProductIds = selectedSet;
      await this.loadThatchSetup();
      this.initialSelectedBenefitProductIds = this.payrollBenefitsAssignmentService.getPersistedSelectedProductIds(
        [...selectedSet, ...this.preservedNativeProductIds],
        this.companyProductIdsInOrder
      );
      const allowanceControlDisabledProductIds = new Set(
        this.teamCompaniesService.getAllowanceControlDisabledProductIds(this.companyInfo)
      );
      const allowanceEligibleProductIds = new Set(
        Array.isArray(this.teamInfo?.metadata?.allowanceEligibleProductIds)
          ? this.teamInfo.metadata.allowanceEligibleProductIds
          : []
      );
      this.benefitProducts = this.benefitProducts.map((product) => ({
        ...product,
        allowanceControlEnabled: !allowanceControlDisabledProductIds.has(product.id),
        allowanceEligible: allowanceEligibleProductIds.has(product.id),
      }));
      this.initialAllowanceEligibleProductIds = this.getAllowanceEligibleProductIds();
      this.loadMissingBenefitProductImages();
      this.initialProductPayrollDeductionMapSnapshot = this.serializeProductPayrollDeductionMap();
      this.reimbursementProductLookup = this.buildReimbursementProductLookup(
        (benefitsLoad.assignableProducts || []).filter((product) => {
          return !availableBenefitProductIds || availableBenefitProductIds.has(product.id);
        }),
        this.benefitProducts
      );
      this.hasLoadedBenefitProducts = true;
      await payrollDeductionFeatureFlagPromise;
      void this.loadProductSourceTeams();
    } catch (error) {
      console.error('Unable to load benefit products', error);
    } finally {
      this.isLoadingBenefitsProducts = false;
    }
  }

  private loadMissingBenefitProductImages(): void {
    this.missingBenefitProductImageLoadVersion++;
    this.missingBenefitProductImageActiveRequests = 0;
    this.missingBenefitProductImageQueue = this.benefitProducts.filter((product) =>
      !this.getTrimmedText(product?.imageUrl) && !!this.getTrimmedText(product?.stripeProductId)
    );
    this.fillMissingBenefitProductImageQueue(this.missingBenefitProductImageLoadVersion);
  }

  private fillMissingBenefitProductImageQueue(loadVersion: number): void {
    while (
      loadVersion === this.missingBenefitProductImageLoadVersion
      && this.missingBenefitProductImageActiveRequests < this.missingBenefitProductImageMaxConcurrentLoads
      && this.missingBenefitProductImageQueue.length > 0
    ) {
      const product = this.missingBenefitProductImageQueue.shift();
      if (!product) {
        continue;
      }

      this.missingBenefitProductImageActiveRequests++;
      void this.loadMissingBenefitProductImage(product, loadVersion)
        .finally(() => {
          if (loadVersion !== this.missingBenefitProductImageLoadVersion) {
            return;
          }
          this.missingBenefitProductImageActiveRequests = Math.max(
            0,
            this.missingBenefitProductImageActiveRequests - 1
          );
          this.fillMissingBenefitProductImageQueue(loadVersion);
        });
    }
  }

  private async loadMissingBenefitProductImage(product: BenefitProductItem, loadVersion: number): Promise<void> {
    const productId = this.getTrimmedText(product?.id);
    const stripeProductId = this.getTrimmedText(product?.stripeProductId);
    if (!productId || !stripeProductId || loadVersion !== this.missingBenefitProductImageLoadVersion) {
      return;
    }

    const imageUrl = await this.getStripeProductImageUrl(stripeProductId).catch(() => '');
    if (!imageUrl || loadVersion !== this.missingBenefitProductImageLoadVersion) {
      return;
    }

    this.benefitProducts = this.benefitProducts.map((candidate) => {
      if (candidate.id !== productId || this.getTrimmedText(candidate.imageUrl)) {
        return candidate;
      }
      return {
        ...candidate,
        imageUrl,
      };
    });
  }

  private getStripeProductImageUrl(stripeProductId: string): Promise<string> {
    const normalizedStripeProductId = this.getTrimmedText(stripeProductId);
    const existingRequest = this.stripeProductImageRequests.get(normalizedStripeProductId);
    if (existingRequest) {
      return existingRequest;
    }

    const request = this.stripeService.getProduct(normalizedStripeProductId)
      .then((stripeProduct: any) => this.getImageUrlCandidates(stripeProduct?.images)[0] || '')
      .catch(() => '');
    this.stripeProductImageRequests.set(normalizedStripeProductId, request);
    return request;
  }

  private getImageUrlCandidates(value: any): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((candidate) => this.getTrimmedText(candidate))
      .filter((candidate) => !!candidate);
  }

  private getTrimmedText(value: any): string {
    return `${value ?? ''}`.trim();
  }

  private async loadPayrollDeductionFeatureFlag(): Promise<void> {
    if (this.payrollDeductionFeatureFlagRequest) {
      await this.payrollDeductionFeatureFlagRequest;
      return;
    }

    this.payrollDeductionFeatureFlagRequest = this.managedFeatureFlagsService.isEnabled(
      PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG,
      'team-admin',
      {
        userId: this.sessionService.getUserId() || undefined,
      },
    )
      .then((enabled) => {
        this.isPayrollDeductionFeatureEnabled = enabled;
      })
      .catch(() => {
        this.isPayrollDeductionFeatureEnabled = false;
      })
      .finally(() => {
        this.payrollDeductionFeatureFlagRequest = undefined;
      });

    await this.payrollDeductionFeatureFlagRequest;
  }

  private async preloadPayrollDeductionCodesIfNeeded(): Promise<void> {
    if (!this.isPayrollDeductionAvailable) {
      return;
    }

    if (this.benefitProducts.some((product) => product.payrollDeductionEnabled)) {
      await this.ensurePayrollDeductionCodesLoaded();
    }
  }

  private async requestGroupProductOnboarding(product: BenefitProductItem): Promise<void> {
    const companyId = `${this.data?.companyId || ''}`.trim();
    if (!companyId || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }

    const dialogRef = this.dialog.open<
      GroupProductOnboardingRequestDialogComponent,
      { providerName: string; planName: string },
      GroupProductOnboardingRequestDialogResult
    >(GroupProductOnboardingRequestDialogComponent, {
      width: '640px',
      maxWidth: '96vw',
      autoFocus: false,
      data: {
        providerName: product.vendorName || product.productName || 'Provider',
        planName: product.productName || '',
      },
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result?.send) {
      return;
    }

    this.pendingOnboardingRequestProductIds.add(product.id);
    try {
      await this.teamCompaniesService.submitGroupProductOnboardingRequest(
        companyId,
        product.id,
        result.note || '',
      );
      this.selectedBenefitProductIds.delete(product.id);
      this.selectedBenefitProductIds = new Set(this.selectedBenefitProductIds);
      this.benefitProducts = this.benefitProducts.map((item) => item.id === product.id
        ? {
          ...item,
          approvalStatus: 'pending',
          approvalLabel: 'Awaiting Wallit Confirmation',
          approvalDisabledReason:
            'Wallit has been notified and will reach out within 2 business days before this plan can be enabled.',
          canRequestApproval: false,
        }
        : item
      );
      this.hasSubmittedGroupProductOnboardingRequest = true;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Wallit has been notified and this product approval is now awaiting confirmation.',
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.error || 'Unable to submit the product approval request.',
      });
    } finally {
      this.pendingOnboardingRequestProductIds.delete(product.id);
    }
  }

  private async loadBonuses(): Promise<void> {
    const teamId = this.data?.teamId;
    if (!teamId) {
      return;
    }

    this.isLoadingBonuses = true;

    try {
      const programs = await this.teamProgramsService.getPrograms(teamId);
      if (!Array.isArray(programs)) throw new Error('Bonus settings unavailable');

      this.bonusSettings = this.bonusSettings.map(setting => {
        const thisProgram = programs.find((program: any) => program?.type === setting.type);
        if (!thisProgram) {
          return { ...setting, id: '', enabled: false, message: '' };
        }

        return {
          ...setting,
          id: thisProgram?.id ?? '',
          enabled: true,
          amount: this.toValidMoneyAmount(thisProgram?.amount),
          message: thisProgram?.message ?? '',
        };
      });
      this.bonusSettings.forEach(setting => {
        this.initialBonusSettings.set(setting.type, this.serializeBonusSetting(setting));
      });
      this.hasLoadedBonuses = true;
    } catch (error) {
      this.loadError = 'Benefits settings could not be loaded. Retry before saving.';
      console.error('Unable to load bonus settings', error);
    } finally {
      this.isLoadingBonuses = false;
    }
  }

  private async loadReimbursedCategories(): Promise<void> {
    const teamId = this.data?.teamId;
    if (!teamId) {
      return;
    }

    this.isLoadingReimbursed = true;

    try {
      const [categories, team] = await Promise.all([this.teamsService.getCategories(), this.teamsService.getTeamInfo(teamId)]);
      if (!team?.id || team.id !== teamId || !Array.isArray(categories)) throw new Error('Team settings unavailable');
      const selectedCategories = Array.isArray(team?.categories) ? team.categories : [];
      const loadedCustomCategories = this.normalizeCustomReimbursementCategories(
        team?.metadata?.customReimbursementCategories
      );
      const loadedExcludedTransactionTerms = this.normalizeExcludedTransactionTerms(
        team?.metadata?.excludedTransactions
      );
      const loadedIncludedTransactionTerms = this.normalizeIncludedTransactionTerms(
        team?.metadata?.includedTransactions
      );

      this.currentTeamPlan = team?.plan || Plan.Free;
      this.teamInfo = team;
      this.reimbursementsEnabled = this.initialReimbursementsEnabled = !team.disabled;
      this.selectedAllowance = this.initialAllowance = Number(team.amount || 0);
      this.initialReimbursedCategories = JSON.parse(JSON.stringify(selectedCategories));
      this.reimbursedCategories = (categories || []).map((category: Category) => ({
        taxable: !!category.taxable,
        toolTip: category.toolTip,
        category: category.name,
        selected: selectedCategories.includes(category.name),
      }));
      this.customReimbursementCategories = loadedCustomCategories;
      this.initialCustomReimbursementCategoriesSnapshot =
        this.serializeCustomReimbursementCategories(loadedCustomCategories);
      this.excludedTransactionTerms = loadedExcludedTransactionTerms;
      this.initialExcludedTransactionTermsSnapshot =
        this.serializeExcludedTransactionTerms(loadedExcludedTransactionTerms);
      this.includedTransactionTerms = loadedIncludedTransactionTerms;
      this.initialIncludedTransactionTermsSnapshot =
        this.serializeIncludedTransactionTerms(loadedIncludedTransactionTerms);
      this.pendingCustomReimbursementCategoryResolutions = [];
      this.hasLoadedReimbursedCategories = true;
    } catch (error) {
      this.loadError = 'Benefits settings could not be loaded. Retry before saving.';
      console.error('Unable to load reimbursed categories', error);
    } finally {
      this.isLoadingReimbursed = false;
    }
  }

  private async persistReimbursementStatus(teamId: string): Promise<void> {
    if (!this.hasLoadedReimbursedCategories || this.reimbursementsEnabled === this.initialReimbursementsEnabled) {
      return;
    }

    await this.teamsService.modifyTeam(teamId, { disabled: !this.reimbursementsEnabled });
    this.initialReimbursementsEnabled = this.reimbursementsEnabled;
    this.teamInfo = { ...this.teamInfo, disabled: !this.reimbursementsEnabled };
    this.savedSections.add('Reimbursement setting');
  }

  private async persistReimbursedCategories(teamId: string): Promise<void> {
    if (!this.hasLoadedReimbursedCategories) {
      return;
    }

    const selectedCategories = this.reimbursedCategories
      .filter(category => category.selected)
      .map(category => category.category);
    const categoriesChanged = this.reimbursementsEnabled
      && JSON.stringify(this.initialReimbursedCategories) !== JSON.stringify(selectedCategories);
    const allowanceChanged = this.data?.teamManager && this.selectedAllowance !== this.initialAllowance;
    if (!categoriesChanged && !allowanceChanged) {
      return;
    }

    // Enabled teams synchronize on save; other teams retain the legacy sequence.
    // Product selection is never part of this payload.
    const saved = await this.teamsService.modifyTeam(teamId, {
      ...(this.reimbursementsEnabled ? { categories: selectedCategories } : {}),
      ...(allowanceChanged ? { amount: this.selectedAllowance } : {}),
    });
    if (categoriesChanged) this.savedSections.add('Reimbursement categories');
    if (allowanceChanged) this.savedSections.add('Team allowance amount');
    if (allowanceChanged && (!saved?.allowanceFunding || saved.allowanceFunding.status === 'legacy')) {
      const paymentMethod = await this.teamsService.getPaymentMethod(this.teamInfo);
      if (paymentMethod) {
        const fee = await this.feeService.getPaymentMethodFee(this.teamInfo);
        const members = await this.teamsService.getTeamMembers(teamId);
        const customerId = await this.teamsService.getCustomerId(this.teamInfo);
        await this.stripeService.changeAllowance(customerId, this.selectedAllowance,
          members.filter(member => member.status === 'active').length, fee.toString(), teamId, paymentMethod);
        this.savedSections.add('Allowance billing');
      }
    }
    this.initialAllowance = this.selectedAllowance;
    if (this.reimbursementsEnabled) this.initialReimbursedCategories = JSON.parse(JSON.stringify(selectedCategories));
  }

  private async persistBenefitProducts(teamId: string): Promise<void> {
    if (this.data?.teamManager) return;
    if (!this.hasLoadedBenefitProducts || !this.teamInfo) {
      return;
    }

    const selectedProductIds = [
      ...Array.from(this.selectedBenefitProductIds),
      ...this.preservedNativeProductIds,
    ];
    const selectedIds = this.payrollBenefitsAssignmentService.getPersistedSelectedProductIds(
      selectedProductIds,
      this.companyProductIdsInOrder,
      this.payrollBenefitsAssignmentService.getAlwaysActiveBenefitProductIds(this.benefitProducts),
    );
    const hasChanged =
      JSON.stringify(this.initialSelectedBenefitProductIds) !== JSON.stringify(selectedIds);
    const allowanceEligibleProductIds = this.getAllowanceEligibleProductIds();
    const allowanceHasChanged =
      JSON.stringify(this.initialAllowanceEligibleProductIds) !== JSON.stringify(allowanceEligibleProductIds);
    if (!hasChanged && !allowanceHasChanged) {
      return;
    }

    const persisted = await this.payrollBenefitsAssignmentService.persistTeamBenefitSettings(
      teamId,
      this.teamInfo,
      selectedProductIds,
      this.companyProductIdsInOrder,
      this.payrollBenefitsAssignmentService.getAlwaysActiveBenefitProductIds(this.benefitProducts),
      allowanceEligibleProductIds,
      { persistActiveProducts: hasChanged },
    );
    this.savedSections.add('Benefit products');
    this.teamInfo = { ...(this.teamInfo || {}), metadata: persisted.metadata };
    this.initialSelectedBenefitProductIds = [...persisted.persistedIds];
    this.initialAllowanceEligibleProductIds = [...allowanceEligibleProductIds];
  }

  private getAllowanceEligibleProductIds(): string[] {
    const currentEligibleProductIds = new Set(
      Array.isArray(this.teamInfo?.metadata?.allowanceEligibleProductIds)
        ? this.teamInfo.metadata.allowanceEligibleProductIds
        : []
    );
    this.benefitProducts.forEach((product) => {
      if (product.allowanceControlEnabled !== true) {
        return;
      }
      if (product.allowanceEligible === true) {
        currentEligibleProductIds.add(product.id);
      } else {
        currentEligibleProductIds.delete(product.id);
      }
    });

    return this.companyProductIdsInOrder.filter((productId) => currentEligibleProductIds.has(productId));
  }

  private async validateAndPersistPayrollDeductionConfig(): Promise<boolean> {
    if (!this.hasLoadedBenefitProducts || !this.companyInfo?.id || !this.isPayrollDeductionAvailable) {
      return true;
    }

    const hasEnabledPayrollDeductionProducts = this.benefitProducts
      .some((product) => product.payrollDeductionEnabled === true);

    if (hasEnabledPayrollDeductionProducts) {
      await this.ensurePayrollDeductionCodesLoaded();
      const firstInvalidProduct = this.benefitProducts.find((product) => !!this.getPayrollDeductionValidationMessage(product));
      if (firstInvalidProduct) {
        const productLabel = firstInvalidProduct.vendorName || firstInvalidProduct.productName || 'This product';
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: `${productLabel}: ${this.getPayrollDeductionValidationMessage(firstInvalidProduct)}`,
        });
        return false;
      }
    }

    const serializedMap = this.serializeProductPayrollDeductionMap();
    if (serializedMap === this.initialProductPayrollDeductionMapSnapshot) {
      return true;
    }

    const nextProductPayrollDeductionMap = JSON.parse(serializedMap);
    const updatedCompany = await this.teamCompaniesService.modifyCompanyPayrollConfig(
      this.companyInfo.id,
      (_latestPayrollConfig, latestCompany) => this.teamCompaniesService.buildProductPayrollDeductionPayrollConfig(
        latestCompany,
        nextProductPayrollDeductionMap,
      ),
    );
    const payrollConfig = updatedCompany?.payrollConfig || this.companyInfo?.payrollConfig || {};

    this.companyInfo = {
      ...(this.companyInfo || {}),
      ...(updatedCompany || {}),
      payrollConfig,
      resolvedPayrollConfig: {
        ...(this.companyInfo?.resolvedPayrollConfig || {}),
        ...(updatedCompany?.resolvedPayrollConfig || {}),
      },
    };

    if (Object.keys(nextProductPayrollDeductionMap).length > 0) {
      this.companyInfo.resolvedPayrollConfig.productPayrollDeductionMap = nextProductPayrollDeductionMap;
    } else {
      delete this.companyInfo.resolvedPayrollConfig.productPayrollDeductionMap;
    }

    this.initialProductPayrollDeductionMapSnapshot = serializedMap;
    return true;
  }

  private getReimbursementUpdateMetadata(): any {
    const metadata = { ...(this.teamInfo?.metadata || {}) };
    // PUT /teams replaces metadata, but preserves stored allowance eligibility
    // when omitted. Resending it would validate an unrelated product edit.
    delete metadata.allowanceEligibleProductIds;
    return metadata;
  }

  private async persistCustomReimbursementCategories(teamId: string): Promise<void> {
    if (!this.hasLoadedReimbursedCategories) {
      return;
    }

    const serialized = this.serializeCustomReimbursementCategories(this.customReimbursementCategories);
    const hasPendingResolutions = this.pendingCustomReimbursementCategoryResolutions.length > 0;
    if (serialized === this.initialCustomReimbursementCategoriesSnapshot && !hasPendingResolutions) {
      return;
    }

    const metadata = {
      ...this.getReimbursementUpdateMetadata(),
      customReimbursementCategories: JSON.parse(serialized),
    };
    const payload: any = { metadata };
    if (hasPendingResolutions) {
      payload.customReimbursementCategoryResolutions = this.pendingCustomReimbursementCategoryResolutions.map(
        (resolution) => ({
          sourceCategoryId: resolution.sourceCategoryId,
          action: resolution.action,
          ...(resolution.targetCategoryId ? { targetCategoryId: resolution.targetCategoryId } : {}),
        })
      );
    }

    const response = await this.teamsService.modifyTeam(teamId, payload);
    this.savedSections.add('Custom reimbursement categories');
    this.teamInfo = {
      ...(this.teamInfo || {}),
      metadata: { ...(this.teamInfo?.metadata || {}), ...metadata },
    };
    this.initialCustomReimbursementCategoriesSnapshot = serialized;
    this.pendingCustomReimbursementCategoryResolutions = [];

    const errors = Array.isArray(response?.errors) ? response.errors : [];
    if (errors.length > 0) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: errors.join('. '),
      });
    }
  }

  private async persistExcludedTransactionTerms(teamId: string): Promise<void> {
    if (!this.hasLoadedReimbursedCategories) {
      return;
    }

    const serialized = this.serializeExcludedTransactionTerms(this.excludedTransactionTerms);
    if (serialized === this.initialExcludedTransactionTermsSnapshot) {
      return;
    }

    const metadata = {
      ...this.getReimbursementUpdateMetadata(),
      excludedTransactions: JSON.parse(serialized),
    };

    await this.teamsService.modifyTeam(teamId, { metadata });
    this.savedSections.add('Excluded transactions');
    this.teamInfo = {
      ...(this.teamInfo || {}),
      metadata: { ...(this.teamInfo?.metadata || {}), ...metadata },
    };
    this.excludedTransactionTerms = JSON.parse(serialized);
    this.initialExcludedTransactionTermsSnapshot = serialized;
  }

  private async persistIncludedTransactionTerms(teamId: string): Promise<void> {
    if (!this.hasLoadedReimbursedCategories) {
      return;
    }

    const serialized = this.serializeIncludedTransactionTerms(this.includedTransactionTerms);
    if (serialized === this.initialIncludedTransactionTermsSnapshot) {
      return;
    }

    const metadata = {
      ...this.getReimbursementUpdateMetadata(),
      includedTransactions: JSON.parse(serialized),
    };

    await this.teamsService.modifyTeam(teamId, { metadata });
    this.savedSections.add('Included transactions');
    this.teamInfo = {
      ...(this.teamInfo || {}),
      metadata: { ...(this.teamInfo?.metadata || {}), ...metadata },
    };
    this.includedTransactionTerms = JSON.parse(serialized);
    this.initialIncludedTransactionTermsSnapshot = serialized;
  }

  private serializeBonusSetting(setting: BonusSetting): string {
    return JSON.stringify({
      id: setting.id,
      enabled: setting.enabled,
      amount: this.toValidMoneyAmount(setting.amount),
      message: setting.message || '',
    });
  }

  private async persistBonuses(teamId: string): Promise<void> {
    // Let every request settle before offering retry or allowing the dialog to
    // close. Successful siblings keep their snapshots and are not sent again.
    const results = await Promise.all(this.bonusSettings.map(async setting => {
      try {
        await this.persistBonusSetting(teamId, setting);
        return null;
      } catch (error) {
        return { error };
      }
    }));
    const failure = results.find(result => result !== null);
    if (failure) throw failure.error;
  }

  private async persistBonusSetting(teamId: string, setting: BonusSetting): Promise<void> {
    if (this.initialBonusSettings.get(setting.type) === this.serializeBonusSetting(setting)) return;
    const amount = this.toValidMoneyAmount(setting.amount);

    if (setting.enabled) {
      if (setting.id) {
        await this.teamProgramsService.modifyProgram(teamId, setting.id, { amount, message: setting.message || '' });
      } else {
        const result = await this.teamProgramsService.createProgram(teamId, setting.type, amount, setting.message || '');
        setting.id = result?.id || '';
      }
    } else if (setting.id) {
      await this.teamProgramsService.deleteProgram(teamId, setting.id);
      setting.id = '';
    } else {
      return;
    }
    this.savedSections.add(setting.name);
    this.initialBonusSettings.set(setting.type, this.serializeBonusSetting(setting));
  }

  private toValidMoneyAmount(value: unknown): number {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) {
      return 0;
    }
    return Math.round(amount * 100) / 100;
  }

  private createEmptyCustomReimbursementDraft(): CustomReimbursementDraft {
    return {
      name: '',
      maxAmountDollars: 0,
      rollover: false,
      spendCategories: [],
      vendorProductIds: [],
    };
  }

  private generateCustomReimbursementCategoryClientKey(): string {
    const random = Math.random().toString(36).slice(2, 10);
    return `crc_local_${Date.now()}_${random}`;
  }

  private compareAlphabetically(left: string | undefined | null, right: string | undefined | null): number {
    return this.alphabeticalCollator.compare((left || '').trim(), (right || '').trim());
  }

  private sortBenefitProducts(products: BenefitProductItem[]): BenefitProductItem[] {
    return [...products].sort((a, b) => {
      const primaryComparison = this.compareAlphabetically(
        a.vendorName || a.productName,
        b.vendorName || b.productName
      );
      if (primaryComparison !== 0) {
        return primaryComparison;
      }

      const secondaryComparison = this.compareAlphabetically(a.productName, b.productName);
      if (secondaryComparison !== 0) {
        return secondaryComparison;
      }

      return this.compareAlphabetically(a.description, b.description);
    });
  }

  private sortReimbursedCategories(categories: ReimbursedCategory[]): ReimbursedCategory[] {
    return [...categories].sort((a, b) => this.compareAlphabetically(a.category, b.category));
  }

  private normalizeCustomReimbursementCategories(categories: any): CustomReimbursementCategory[] {
    const now = new Date().toISOString();
    const items = Array.isArray(categories) ? categories : [];
    return items
      .map((item: any) => {
        const name = `${item?.name || ''}`.trim();
        const maxAmountCents = Number(item?.maxAmountCents);
        if (!name || !Number.isFinite(maxAmountCents) || maxAmountCents <= 0) {
          return null;
        }
        return {
          clientKey: `${item?.id || this.generateCustomReimbursementCategoryClientKey()}`,
          ...(item?.id ? { id: `${item.id}` } : {}),
          ...((typeof item?.presetKey === 'string' && item.presetKey.trim())
            ? { presetKey: item.presetKey.trim() as ReimbursementCategoryPresetKey }
            : {}),
          name,
          spendCategories: Array.isArray(item?.spendCategories)
            ? item.spendCategories.map((value: any) => `${value}`).filter((value: string) => !!value)
            : [],
          vendorProductIds: Array.isArray(item?.vendorProductIds)
            ? item.vendorProductIds.map((value: any) => `${value}`).filter((value: string) => !!value)
            : [],
          maxAmountCents,
          frequency: 'monthly',
          rollover: !!item?.rollover,
          archived: !!item?.archived,
          createdAt: item?.createdAt || now,
          updatedAt: item?.updatedAt || now,
        } as CustomReimbursementCategory;
      })
      .filter((item: CustomReimbursementCategory | null): item is CustomReimbursementCategory => !!item);
  }

  private upsertExcludedTransactionTerm(value: any): void {
    const term = `${value || ''}`.trim();
    if (!term) {
      return;
    }

    const normalizedTerm = term.toLowerCase();
    const nextTerms = this.excludedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm)
      .concat(term);
    this.excludedTransactionTerms = this.normalizeExcludedTransactionTerms(nextTerms);
  }

  private upsertIncludedTransactionTerm(value: any): void {
    const term = `${value || ''}`.trim();
    if (!term) {
      return;
    }

    const normalizedTerm = term.toLowerCase();
    const nextTerms = this.includedTransactionTerms
      .filter((item) => item.trim().toLowerCase() !== normalizedTerm)
      .concat(term);
    this.includedTransactionTerms = this.normalizeIncludedTransactionTerms(nextTerms);
  }

  private normalizeExcludedTransactionTerms(value: any): string[] {
    return this.normalizeTransactionTerms(value);
  }

  private normalizeIncludedTransactionTerms(value: any): string[] {
    return this.normalizeTransactionTerms(value);
  }

  private normalizeTransactionTerms(value: any): string[] {
    const rawTerms = this.toTransactionTermList(value);
    const seen = new Set<string>();
    const normalizedTerms: string[] = [];

    rawTerms.forEach((rawTerm) => {
      const term = `${rawTerm || ''}`.trim();
      const normalizedTerm = term.toLowerCase();
      if (!term || seen.has(normalizedTerm)) {
        return;
      }
      seen.add(normalizedTerm);
      normalizedTerms.push(term);
    });

    return normalizedTerms;
  }

  private toTransactionTermList(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.split(',');
    }
    if (!value || typeof value !== 'object') {
      return [];
    }

    return Object.keys(value).reduce((terms: any[], key: string) => {
      const item = value[key];
      if (Array.isArray(item)) {
        return terms.concat(item);
      }
      if (typeof item === 'string') {
        terms.push(...item.split(','));
      } else if (item === true) {
        terms.push(key);
      }
      return terms;
    }, []);
  }

  private serializeExcludedTransactionTerms(terms: string[]): string {
    return JSON.stringify(this.normalizeExcludedTransactionTerms(terms));
  }

  private serializeIncludedTransactionTerms(terms: string[]): string {
    return JSON.stringify(this.normalizeIncludedTransactionTerms(terms));
  }

  private serializeCustomReimbursementCategories(categories: CustomReimbursementCategory[]): string {
    const normalized = [...(categories || [])]
      .map((category) => this.normalizePresetBackedCustomReimbursementCategory(category))
      .map((category) => ({
        ...(category.id ? { id: category.id } : {}),
        ...(category.presetKey ? { presetKey: category.presetKey } : {}),
        name: category.name,
        spendCategories: [...new Set(category.spendCategories)].sort((left, right) => left.localeCompare(right)),
        vendorProductIds: [...new Set(category.vendorProductIds)].sort((left, right) => left.localeCompare(right)),
        maxAmountCents: category.maxAmountCents,
        frequency: 'monthly',
        rollover: category.rollover,
        archived: category.archived,
        createdAt: category.createdAt,
        updatedAt: category.updatedAt,
      }))
      .sort((left: any, right: any) =>
        `${left.id || left.name}`.localeCompare(`${right.id || right.name}`)
      );
    return JSON.stringify(normalized);
  }

  private buildReimbursementProductLookup(
    loadedAssignableProducts: AssignableBenefitProductItem[],
    benefitProducts: BenefitProductItem[],
  ): Map<string, AssignableBenefitProductItem> {
    const byId = new Map<string, AssignableBenefitProductItem>();
    loadedAssignableProducts.forEach((product) => {
      byId.set(product.id, product);
    });
    benefitProducts.forEach((product) => {
      if (byId.has(product.id)) {
        return;
      }
      byId.set(product.id, {
        id: product.id,
        vendorName: product.vendorName || '',
        productName: product.productName || product.id,
        label: `${product.vendorName ? `${product.vendorName} - ` : ''}${product.productName || product.id}`,
      });
    });
    return byId;
  }

  private toFallbackAssignableProduct(productId: string): AssignableBenefitProductItem {
    return {
      id: productId,
      vendorName: '',
      productName: productId,
      label: productId,
    };
  }

  private getBenefitProductById(productId: string): BenefitProductItem | undefined {
    return this.benefitProducts.find((product) => product.id === productId);
  }

  private async ensurePayrollDeductionCodesLoaded(forceReload = false): Promise<void> {
    if (!this.companyInfo?.id || !this.isPayrollDeductionAvailable) {
      return;
    }

    if (!forceReload && this.payrollDeductionCodesRequest) {
      await this.payrollDeductionCodesRequest;
      return;
    }

    if (!forceReload && this.hasAttemptedPayrollDeductionCodesLoad) {
      return;
    }

    this.isLoadingPayrollDeductionCodes = true;
    this.hasAttemptedPayrollDeductionCodesLoad = true;
    this.payrollDeductionCodesLoadError = '';

    this.payrollDeductionCodesRequest = this.payrollService.getDeductionCodesForPayrollProviderCompany(this.companyInfo.id)
      .then((codes) => {
        this.payrollDeductionCodes = [...(codes || [])]
          .filter((code): code is UkgDeductionCode => !!`${code?.code || ''}`.trim())
          .map((code) => ({
            code: `${code.code || ''}`.trim(),
            description: `${code.description || ''}`.trim(),
            preTax: code.preTax === true,
          }))
          .sort((left, right) => {
            const leftLabel = `${left.code} ${left.description}`.trim();
            const rightLabel = `${right.code} ${right.description}`.trim();
            return leftLabel.localeCompare(rightLabel);
          });
        this.syncPayrollDeductionDescriptionsFromOptions();
      })
      .catch((error: any) => {
        this.payrollDeductionCodes = [];
        this.payrollDeductionCodesLoadError =
          error?.error?.error
          || error?.error?.message
          || error?.message
          || 'Unable to load deduction codes from UKG Ready.';
      })
      .finally(() => {
        this.isLoadingPayrollDeductionCodes = false;
        this.payrollDeductionCodesRequest = undefined;
      });

    await this.payrollDeductionCodesRequest;
  }

  private syncPayrollDeductionDescriptionsFromOptions(): void {
    this.benefitProducts = this.benefitProducts.map((product) => {
      const selectedCode = `${product?.payrollDeductionCode || ''}`.trim();
      if (!selectedCode) {
        return product;
      }

      const matchedCode = this.payrollDeductionCodes.find((option) => option.code === selectedCode);
      if (!matchedCode) {
        return product;
      }

      return {
        ...product,
        payrollDeductionCode: matchedCode.code,
        payrollDeductionCodeDescription: matchedCode.description || product.payrollDeductionCodeDescription || '',
      };
    });
  }

  private serializeProductPayrollDeductionMap(): string {
    return JSON.stringify(
      this.payrollBenefitsAssignmentService.buildProductPayrollDeductionMap(this.benefitProducts)
    );
  }

  private getErrorMessage(error: any): string {
    return error?.data?.error
      || error?.error?.error
      || error?.error?.message
      || error?.message
      || 'Unable to load team products.';
  }

}
